// Single source of truth for the Firebase SDK initialisation.
// Imported by every surface (admin, operator, display, live). The public live
// view does NOT call signInAnonymously — Firestore rules permit anonymous
// reads of the public-safe fields, which is what makes the surface embeddable.

import { initializeApp, type FirebaseOptions } from "firebase/app";
import {
  getAuth,
  connectAuthEmulator,
  signInAnonymously,
  type Auth,
} from "firebase/auth";
import {
  initializeFirestore,
  connectFirestoreEmulator,
  persistentLocalCache,
  persistentMultipleTabManager,
  type Firestore,
} from "firebase/firestore";
import { getStorage, connectStorageEmulator, type FirebaseStorage } from "firebase/storage";
import { getFunctions, connectFunctionsEmulator, type Functions } from "firebase/functions";

// All env vars come from `.env` (see `.env.example`). They are public values
// (not secrets) — the security model lives in firestore.rules + Cloud Functions.
// TODO: env — fill in `.env` from the Firebase console before first dev run.
const config: FirebaseOptions = {
  apiKey: import.meta.env.VITE_FB_API_KEY,
  authDomain: import.meta.env.VITE_FB_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FB_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FB_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FB_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FB_APP_ID,
};

if (!config.projectId) {
  // Fail loud and early — beats a confusing "permission denied" 10 surfaces later.
  // eslint-disable-next-line no-console
  console.error(
    "[firebase] VITE_FB_PROJECT_ID is empty. Copy .env.example to .env and fill in the Firebase web config."
  );
}

export const app = initializeApp(config);

// Persistent local cache so a brief disconnect doesn't blank the operator grid.
// multiTabManager is important: the same browser may have admin + operator open.
export const db: Firestore = initializeFirestore(app, {
  localCache: persistentLocalCache({ tabManager: persistentMultipleTabManager() }),
});

export const auth: Auth = getAuth(app);
export const storage: FirebaseStorage = getStorage(app);
// All callable functions live in europe-west2 (London) — see PROJECT CONFIG.
export const functions: Functions = getFunctions(app, "europe-west2");

// Wire emulators when explicitly requested via env. Guarded so prod can never
// accidentally connect to localhost.
if (import.meta.env.VITE_USE_EMULATORS === "1") {
  // eslint-disable-next-line no-console
  console.info("[firebase] Using local emulators");
  connectAuthEmulator(auth, "http://localhost:9099", { disableWarnings: true });
  connectFirestoreEmulator(db, "localhost", 8080);
  connectStorageEmulator(storage, "localhost", 9199);
  connectFunctionsEmulator(functions, "localhost", 5001);
}

/**
 * Anonymous sign-in used by the operator tablets so Firestore rules can
 * distinguish "has *some* auth state" from "fully public anonymous reader".
 * The actual booking authorisation lives in Cloud Functions checking the
 * agent code, NOT in this auth state. Public live view never calls this.
 */
export async function ensureAnonAuth(): Promise<void> {
  if (auth.currentUser) return;
  await signInAnonymously(auth);
}
